import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { getEmailSettings, saveEmailSettings, testEmailSettings } from "../../api/integrations";
import Button from "../../components/common/Button";
import PageHeaderSection from "../../components/layout/PageHeaderSection";
import PageLoadingShell from "../../components/layout/PageLoadingShell";
import { useBranding } from "../../context/BrandingContext";
import { TextInput, CheckboxField } from "../../components/common/form";
import { useUnsavedChanges } from "../../context/UnsavedChangesContext";
import { formatDateTime } from "../../utils/regionalFormat";
import { apiErrorMessage } from '../../utils/apiError';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidEmail(value) {
    return EMAIL_RE.test(String(value || "").trim());
}

function StatusChip({ on, t }) {
    return (
        <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${
                on ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"
            }`}
        >
            {on ? t("settings.report.statusOn") : t("settings.report.statusOff")}
        </span>
    );
}

export default function EmailSettings() {
    const { t } = useTranslation();
    const { branding } = useBranding();
    const [settings, setSettings] = useState(null);
    const [newPassword, setNewPassword] = useState("");
    const [testRecipient, setTestRecipient] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [warning, setWarning] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const s = await getEmailSettings();
            setSettings(s);
            setTestRecipient((prev) => prev || branding?.supportEmail || "");
        } catch {
            setError(t("settings.email.loadFailed"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        if (branding?.supportEmail && !testRecipient) {
            setTestRecipient(branding.supportEmail);
        }
    }, [branding?.supportEmail, testRecipient]);

    const update = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));

    const passwordConfigured = settings?.smtpPassword === "CONFIGURED";
    const canEnable =
        settings?.configured ||
        ((passwordConfigured || newPassword.trim()) && settings?.smtpHost && settings?.fromEmail);
    const canSave = !settings?.enabled || canEnable;

    const dirtyValue = settings ? { settings, newPassword } : null;
    const persist = async () => {
        const payload = {
            ...settings,
            smtpPassword: newPassword.trim() ? newPassword.trim() : settings.smtpPassword,
        };
        const saved = await saveEmailSettings(payload);
        setSettings(saved);
        setNewPassword("");
        return { settings: saved, newPassword: "" };
    };
    const { markSaved } = useUnsavedChanges(dirtyValue, {
        enabled: !!settings,
        onSave: async () => {
            await persist();
        },
    });

    const save = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        setError("");
        setSuccess("");
        setWarning("");
        try {
            const next = await persist();
            markSaved(next);
            setSuccess(t("settings.email.saveSuccess"));
        } catch (err) {
            setError(apiErrorMessage(err, t("settings.email.saveFailed")));
        } finally {
            setSaving(false);
        }
    };

    const testRecipientValid = isValidEmail(testRecipient);
    const testRecipientError =
        testRecipient.trim() && !testRecipientValid ? t("forms.validation.emailInvalid") : "";

    const sendTest = async () => {
        setError("");
        setSuccess("");
        setWarning("");
        if (!isValidEmail(testRecipient)) {
            setError(t("forms.validation.emailInvalid"));
            return;
        }
        setTesting(true);
        try {
            if (!settings?.configured && !newPassword.trim()) {
                throw new Error(t("settings.email.saveBeforeTest"));
            }
            if (!settings?.configured || newPassword.trim()) {
                const next = await persist();
                markSaved(next);
            }
            const result = await testEmailSettings(testRecipient.trim());
            const refreshed = await getEmailSettings();
            setSettings(refreshed);
            markSaved({ settings: refreshed, newPassword: "" });
            if (result?.sentFolderVerified) {
                setSuccess(t("settings.email.testVerifiedSent", {
                    seconds: Math.max(1, Math.round((result.verificationWaitMs || 0) / 1000)),
                    defaultValue: "Verified: message accepted by SMTP and found in Sent folder ({{seconds}}s). Ask the recipient to check inbox and spam.",
                }));
            } else if (result?.smtpAccepted && result?.verificationMethod === "SMTP_ONLY") {
                setWarning(t("settings.email.testSmtpOnly", {
                    defaultValue: "SMTP accepted the message, but delivery was not fully verified.",
                }));
            } else {
                setWarning(t("settings.email.testCompleted", {
                    defaultValue: "Test completed. Delivery could not be fully verified — check spam/promotions.",
                }));
            }
        } catch (err) {
            const refreshed = await getEmailSettings().catch(() => null);
            if (refreshed) {
                setSettings(refreshed);
                markSaved({ settings: refreshed, newPassword: "" });
            }
            setError(apiErrorMessage(err, t("settings.email.testFailed")));
        } finally {
            setTesting(false);
        }
    };

    const formatLastTestAt = (iso) => {
        if (!iso) return null;
        try {
            return formatDateTime(iso);
        } catch {
            return iso;
        }
    };

    const statusLabel = useMemo(() => {
        if (!settings) return "";
        if (settings.enabled) return t("settings.email.modeEnabled");
        if (settings.configured) return t("settings.email.modeConfigured");
        return t("settings.email.modeIncomplete");
    }, [settings, t]);

    if (loading) {
        return (
            <PageLoadingShell
                variant="page-shell-form"
                title={t("settings.email.title")}
                subtitle={t("settings.email.loadingSubtitle")}
            />
        );
    }

    if (!settings) {
        return (
            <div className="page-shell-form">
                <PageHeaderSection
                    eyebrow={t("nav.system")}
                    title={t("settings.email.title")}
                    subtitle={t("settings.email.subtitle")}
                />
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="status">
                    {error || t("settings.email.loadFailed")}
                </div>
            </div>
        );
    }

    return (
        <div className="page-shell-form">
            <PageHeaderSection
                eyebrow={t("nav.system")}
                title={t("settings.email.title")}
                subtitle={t("settings.email.subtitle")}
            />

            <form onSubmit={save} className="space-y-6">
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                    <Trans
                        i18nKey="settings.email.infoBanner"
                        components={{
                            strong: <strong />,
                            1: <Link to="/admin/branding" className="font-medium text-primary-600 hover:underline" />,
                            2: (
                                <Link
                                    to="/admin/notification-preferences"
                                    className="font-medium text-primary-600 hover:underline"
                                />
                            ),
                        }}
                    />
                </div>

                {success && (
                    <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                        {success}
                    </div>
                )}
                {warning && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
                        {warning}
                    </div>
                )}
                {error && (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {error}
                    </div>
                )}

                {/* Step 1 — SMTP */}
                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                                    {t("settings.email.stepOne")}
                                </p>
                                <h2 className="mt-1 text-lg font-semibold text-slate-900">
                                    {t("settings.email.smtpSection")}
                                </h2>
                                <p className="mt-1 max-w-2xl text-sm text-slate-600">
                                    {t("settings.email.smtpSectionDesc")}
                                </p>
                            </div>
                            <div className="shrink-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-center">
                                <p className="text-xs font-medium text-slate-500">{t("settings.email.statusLabel")}</p>
                                <p className="text-sm font-semibold text-slate-900">{statusLabel}</p>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4 px-5 py-5 sm:px-6">
                        <div
                            className={`flex items-center justify-between gap-4 rounded-xl border p-4 ${
                                settings.enabled
                                    ? "border-primary-200 bg-primary-50/40"
                                    : "border-slate-200 bg-slate-50/60"
                            }`}
                        >
                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                    <p className="text-sm font-semibold text-slate-900">{t("settings.email.enableTitle")}</p>
                                    <StatusChip on={!!settings.enabled} t={t} />
                                </div>
                                <p className="mt-1 text-sm text-slate-600">{t("settings.email.enableHint")}</p>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={settings.enabled}
                                onClick={() => update("enabled", !settings.enabled)}
                                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                                    settings.enabled ? "bg-primary-600" : "bg-slate-200"
                                }`}
                            >
                                <span
                                    className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition ${
                                        settings.enabled ? "translate-x-5" : "translate-x-0"
                                    }`}
                                />
                            </button>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                            <TextInput
                                label={t("settings.email.smtpHost")}
                                placeholder="smtp.yourprovider.com"
                                value={settings.smtpHost || ""}
                                onChange={(e) => update("smtpHost", e.target.value)}
                            />
                            <TextInput
                                label={t("settings.email.smtpPort")}
                                type="number"
                                value={settings.smtpPort || 587}
                                onChange={(e) => update("smtpPort", Number(e.target.value))}
                            />
                            <TextInput
                                label={t("settings.email.smtpUsername")}
                                value={settings.smtpUsername || ""}
                                onChange={(e) => update("smtpUsername", e.target.value)}
                            />
                            <TextInput
                                label={t("settings.email.smtpPassword")}
                                type="password"
                                placeholder={
                                    passwordConfigured
                                        ? t("settings.email.passwordConfigured")
                                        : t("settings.email.passwordRequired")
                                }
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                autoComplete="new-password"
                            />
                        </div>

                        <CheckboxField
                            layout="toggle-card"
                            label={t("settings.email.useStarttls")}
                            description={t("settings.email.useStarttlsDesc")}
                            checked={!!settings.useStarttls}
                            onChange={(e) => update("useStarttls", e.target.checked)}
                        />

                        {settings.enabled && !canSave && (
                            <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                                {t("settings.email.enableWarning")}
                            </p>
                        )}
                    </div>
                </section>

                {/* Step 2 — From identity */}
                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
                        <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                            {t("settings.email.stepTwo")}
                        </p>
                        <h2 className="mt-1 text-lg font-semibold text-slate-900">
                            {t("settings.email.fromSection")}
                        </h2>
                        <p className="mt-1 max-w-2xl text-sm text-slate-600">
                            {t("settings.email.fromSectionDesc")}
                        </p>
                    </div>
                    <div className="grid gap-4 px-5 py-5 md:grid-cols-2 sm:px-6">
                        <TextInput
                            label={t("settings.email.fromEmail")}
                            type="email"
                            placeholder="notifications@youracademy.com"
                            value={settings.fromEmail || ""}
                            onChange={(e) => update("fromEmail", e.target.value)}
                        />
                        <TextInput
                            label={t("settings.email.fromName")}
                            placeholder={t("settings.email.fromNamePlaceholder")}
                            value={settings.fromName || ""}
                            onChange={(e) => update("fromName", e.target.value)}
                        />
                        <TextInput
                            label={t("settings.email.replyTo")}
                            type="email"
                            placeholder={t("settings.email.replyToPlaceholder")}
                            value={settings.replyToEmail || ""}
                            onChange={(e) => update("replyToEmail", e.target.value)}
                            fieldClassName="md:col-span-2"
                        />
                    </div>
                </section>

                {/* Step 3 — Test */}
                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
                        <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                            {t("settings.email.stepThree")}
                        </p>
                        <h2 className="mt-1 text-lg font-semibold text-slate-900">
                            {t("settings.email.testSection")}
                        </h2>
                        <p className="mt-1 max-w-2xl text-sm text-slate-600">
                            {t("settings.email.testSectionDesc")}
                        </p>
                    </div>
                    <div className="space-y-4 px-5 py-5 sm:px-6">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                            <TextInput
                                label={t("settings.email.testRecipient")}
                                type="email"
                                value={testRecipient}
                                onChange={(e) => setTestRecipient(e.target.value)}
                                error={testRecipientError}
                                fieldClassName="flex-1"
                            />
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={sendTest}
                                disabled={testing || !testRecipientValid}
                            >
                                {testing ? t("settings.email.verifying") : t("settings.email.sendTest")}
                            </Button>
                        </div>

                        {settings.lastTestOk === true && (
                            <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                                {t("settings.email.lastTestPassed")}
                                {settings.lastTestAt ? ` — ${formatLastTestAt(settings.lastTestAt)}` : ""}.{" "}
                                {t("settings.email.lastTestPassedHint")}
                            </p>
                        )}
                        {settings.lastTestOk === false && (
                            <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                {t("settings.email.lastTestFailed")}
                                {settings.lastTestAt ? ` — ${formatLastTestAt(settings.lastTestAt)}` : ""}.{" "}
                                {t("settings.email.lastTestFailedHint")}
                            </p>
                        )}
                    </div>
                </section>

                <div className="sticky bottom-4 z-10 flex justify-end">
                    <button
                        type="submit"
                        disabled={saving || !canSave}
                        className="rounded-xl bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-lg hover:bg-primary-700 disabled:opacity-50"
                    >
                        {saving ? t("forms.common.saving") : t("settings.email.saveSettings")}
                    </button>
                </div>
            </form>
        </div>
    );
}
